import { useState, type ReactNode } from 'react';

import { Excavator, GearbeltLogo, OnboardingCloseWork, OnboardingTrackWork, OnboardingViewAssets } from '@gearbelt/ui/assets';
import { Button } from '@gearbelt/ui/components';

interface OnboardingSlide {
  title: string;
  subtitle: string;
  illustration: ReactNode;
}

const SLIDES: OnboardingSlide[] = [
  {
    title: 'Welcome to Gearbelt Mobile',
    subtitle: 'Manage your fleet maintenance from the field using our dedicated mobile app.',
    illustration: <Excavator width={253} />,
  },
  {
    title: 'All your assets in one place',
    subtitle: 'Open any asset to view its documents, issues, schedule checks and work orders.',
    illustration: <OnboardingViewAssets />,
  },
  {
    title: "Know what's open and due",
    subtitle: 'See what needs to be done including work orders, issues and upcoming schedule checks.',
    illustration: <OnboardingTrackWork />,
  },
  {
    title: 'Close your work on the go',
    subtitle: 'Create and close work orders, issues and schedule checks, all from the field.',
    illustration: <OnboardingCloseWork />,
  },
];

function PaginationDots({ count, activeIndex }: { count: number; activeIndex: number }) {
  return (
    <div className="flex items-center justify-center gap-2.5" aria-label={`Step ${activeIndex + 1} of ${count}`}>
      {Array.from({ length: count }, (_, dotIndex) => (
        <span key={dotIndex} className={`size-2 rounded-full ${dotIndex === activeIndex ? 'bg-default-blue' : 'bg-grey-3'}`} />
      ))}
    </div>
  );
}

// Introduces Gearbelt Mobile before the AWA→member migration steps so a mechanic
// redirected here understands why they're being asked to set up an account.
export function AWAOnboardingCarousel({ onComplete }: { onComplete: () => void }) {
  const [index, setIndex] = useState(0);
  const isLastSlide = index === SLIDES.length - 1;
  const slide = SLIDES[index];

  function handleNext() {
    if (isLastSlide) {
      onComplete();
    } else {
      setIndex((current) => current + 1);
    }
  }

  return (
    // Figma onboarding frame: 17px horizontal padding, 34px top, 40px gaps, 414px max width.
    <div className="mx-auto flex h-screen w-full max-w-[414px] flex-col items-center gap-10 px-[17px] pt-[34px] pb-9">
      <GearbeltLogo />
      <div className="flex w-full flex-1 flex-col items-center justify-between">
        {/* aria-live so screen readers announce the title+subtitle when Next swaps slides. */}
        <div className="flex w-full flex-col gap-2.5" aria-live="polite" aria-atomic="true">
          <h1 className="text-default-text text-center text-3xl leading-normal font-medium">{slide.title}</h1>
          <p className="text-default-text text-center text-lg leading-normal">{slide.subtitle}</p>
        </div>

        <div className="flex items-center justify-center">{slide.illustration}</div>

        <div className="flex w-full flex-col gap-5">
          <Button type="button" className="w-full text-lg font-medium" onClick={handleNext}>
            {isLastSlide ? 'Get started' : 'Next'}
          </Button>
          <PaginationDots count={SLIDES.length} activeIndex={index} />
        </div>
      </div>
    </div>
  );
}
